/* React */
import React, { useState } from 'react';

/* Translations */
import { useTranslation } from 'react-i18next';

/* Components */
import { Form, Formik } from 'formik';
import { PhotoUploader } from 'lego-dist';

/* Styled Components */
import themes from 'themes';
import {
  CenterInfo,
  CenterImage,
  CenterName,
  Overlay,
  CenterEditWrapperMobile,
  CenterEditWrapperDesktop,
  CenterNameCharCount,
  CenterEditName,
  EditBtn,
  EditActions,
  EditCancelMobile,
  EditCancel,
  EditSaveMobile,
  EditSaveDesktop,
  EditSave,
} from './styles/CenterInfo';

/* Services, Hooks, Constants */
import * as Yup from 'yup';
import { FILES_API_URL, AWS_REGION, S3_BUCKET_FILES, AWS_KEY } from 'config';
import EventReporter from 'appEvents';
import { EventProviders, AppEvents } from 'constants/events';
import useCenters from 'hooks/centers/UseCenters';
import RendererIf from 'components/RendererIf';
import { ActionPermission } from 'lib/specialPermissions';
import useCurrentUser from 'hooks/useCurrentUser';
import { putCenterAvatarQuery } from '../queryConfig';
import { useMutation } from 'react-query';

interface CenterInfoProps {
  center: any;
}

const CenterDetails: React.FC<CenterInfoProps> = ({ center }) => {
  const { t } = useTranslation();
  const { editCenterName } = useCenters();
  const { can } = useCurrentUser();
  const [showEditName, setShowEditName] = useState<boolean>(false);
  const [updName, setUpdName] = useState<string>('');

  /* track characters in input field(s) */
  const [chars, setChars] = useState(0);

  const [putAvatar] = useMutation(putCenterAvatarQuery.resource);

  /* EDIT CENTER functions */
  const handleSubmit = (res: any) => {
    const centerId = center.id;
    const centerData = {
      center: {
        name: res,
      },
    };
    editCenterName(centerId, centerData);
    setUpdName(res);
    setShowEditName(false);
    EventReporter.view(AppEvents.ADD_CENTER(), [EventProviders.MIXPANEL]);
  };

  /* UPLOAD CENTER IMAGE functions */
  const onUploadedAvatarToS3 = ({ s3FilePath, croppedFile }: any) => {
    // eslint-disable-next-line camelcase
    const s3_files_attributes = [
      {
        mime_type: croppedFile.type,
        file_name: s3FilePath,
        status: 'Complete',
        service_urls_attributes: [
          {
            record_type: 'thumbnail_url',
            url: s3FilePath,
          },
        ],
      },
    ];

    putAvatar({
      centerId: center?.id,
      body: {
        center: { s3_files_attributes },
      },
    });
  };

  const onSubmitForm = (formData: any) => {
    handleSubmit(formData.name);
  };

  /* Validation */
  const centerForm = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('validations:FIELD_REQUIRED'))
      .max(70, t('validations:MAX_LENGTH', { amount: 70 })),
  });

  return (
    <CenterInfo disable={!can(ActionPermission.EDIT_CENTER_NAME_AND_PHOTO)}>
      <Formik
        onSubmit={onSubmitForm}
        initialValues={center}
        validationSchema={centerForm}
        validateOnBlur
        validateOnChange
        initialStatus={!!center}
        isInitialValid={!!center}
        render={({ setFieldValue, errors, isValid }: any) => (
          <Form>
            <CenterImage>
              <PhotoUploader
                multipart
                previewModalProps={{ headerColor: themes.colors.newKinedu }}
                src={center?.logo}
                withDefaultIcon={!center?.name}
                defaultLetter={center?.name?.charAt(0).toUpperCase()}
                onCroppedFile={({ uploadFileToS3, croppedFile }: any) => {
                  uploadFileToS3(croppedFile);
                }}
                positiveButtonProps={{
                  children: t('SAVE'),
                }}
                negativeButtonProps={{
                  children: t('CANCEL'),
                }}
                onUploadedFile={({ s3FilePath, croppedFile }: any) => {
                  onUploadedAvatarToS3({ s3FilePath, croppedFile });
                }}
                s3UploaderProps={{
                  apiURL: FILES_API_URL,
                  awsRegion: AWS_REGION,
                  bucket: S3_BUCKET_FILES,
                  aws_key: AWS_KEY,
                }}
                s3path={`${center?.id}/avatars/centers/${Date.now()}_${
                  localStorage?.ACTIVE_CENTER_ID
                }_`}
              />
            </CenterImage>

            {showEditName ? (
              <>
                {/* MOBILE */}
                <Overlay />
                <CenterEditWrapperMobile>
                  <CenterNameCharCount>{`${chars} / 70`}</CenterNameCharCount>
                  <CenterEditName
                    type="text"
                    value={updName !== '' ? updName : center?.name}
                    maxLength={70}
                    onChange={(event: any) => {
                      setChars(event.length);
                      setFieldValue('name', event);
                    }}
                    errorMessage={errors.name}
                  />
                  <EditActions>
                    <EditCancelMobile
                      rounded
                      flat
                      outline
                      onClick={() => {
                        setShowEditName(false);
                        setChars(center.name.length);
                      }}
                    >
                      {t('CANCEL')}
                    </EditCancelMobile>
                    <EditSaveMobile
                      type="submit"
                      disabled={!isValid}
                      rounded
                      flat
                      color={isValid ? '#75B753' : 'disabled'}
                    >
                      {t('SAVE')}
                    </EditSaveMobile>
                  </EditActions>
                </CenterEditWrapperMobile>
                {/* DESKTOP */}
                <CenterEditWrapperDesktop>
                  <CenterNameCharCount>{`${chars} / 70`}</CenterNameCharCount>
                  <CenterEditName
                    type="text"
                    value={updName !== '' ? updName : center?.name}
                    maxLength={70}
                    onChange={(event: any) => {
                      setChars(event.length);
                      setFieldValue('name', event);
                    }}
                    errorMessage={errors.name}
                  />
                  <EditActions>
                    <EditCancel
                      onClick={() => {
                        setShowEditName(false);
                        setChars(center?.name.length);
                      }}
                    />
                    <EditSaveDesktop type="submit" disabled={!isValid}>
                      <EditSave />
                    </EditSaveDesktop>
                  </EditActions>
                </CenterEditWrapperDesktop>
              </>
            ) : (
              <>
                <CenterName>
                  {updName !== '' ? updName : center?.name}
                </CenterName>
                <RendererIf
                  condition={can(ActionPermission.EDIT_CENTER_NAME_AND_PHOTO)}
                >
                  <EditBtn
                    onClick={() => {
                      setChars(center?.name.length);
                      setShowEditName(!showEditName);
                    }}
                  />
                </RendererIf>
              </>
            )}
          </Form>
        )}
      />
    </CenterInfo>
  );
};

export default CenterDetails;
